import React from "react";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";
import { logout } from "utils/accounts";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { User } from "../../features/user";
import { Z_INDEX_MODAL_BACKDROP } from "../../lib/modal-stack";

function getInitials(user: User): string {
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  if (firstName) {
    return firstName.slice(0, 2).toUpperCase();
  }

  if (user.userName) {
    return user.userName.slice(0, 2).toUpperCase();
  }

  return "U";
}

export function HeaderUserMenu({
  calendarUplift = false,
}: {
  calendarUplift?: boolean;
}): React.ReactElement {
  const user: User = useSelector(getUser);

  // Find the collaborator object to get profile image
  const currentCollaborator = user.collaborators?.find(
    (c) => c._id === user._id || c.email === user.email
  );
  const profileImage = currentCollaborator?.profileImage;

  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.userName;

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            "flex items-center justify-center rounded-full cursor-pointer outline-none transition-all",
            "hover:ring-2 hover:ring-blue-200 focus-visible:ring-2 focus-visible:ring-blue-500"
          )}
          data-testid="profile-menu"
          id="dasboard-profile-menu"
          aria-label={`${user.userName} profile menu`}
        >
          <Avatar className="h-8 w-8">
            {profileImage && (
              <AvatarImage src={profileImage} alt={user.userName} />
            )}
            <AvatarFallback
              className={cn(
                calendarUplift
                  ? "bg-blue-50 text-sm font-semibold leading-5 text-[#333333]"
                  : "bg-blue-100 text-xs font-medium text-blue-700"
              )}
            >
              {getInitials(user)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="min-w-[220px] overflow-hidden rounded-md bg-white p-2 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.06)] border border-[rgba(26,24,35,0.08)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:fill-mode-forwards duration-200"
          data-testid="profile-menu-links"
          align="end"
          sideOffset={8}
          /** on top others, but below modal backdrop */
          style={{ zIndex: Z_INDEX_MODAL_BACKDROP - 1 }}
        >
          {/* User Info Section */}
          <div className="flex items-center gap-2 p-2">
            <Avatar className="h-8 w-8 flex-shrink-0">
              {profileImage && (
                <AvatarImage src={profileImage} alt={user.userName} />
              )}
              <AvatarFallback
                className={cn(
                  calendarUplift
                    ? "bg-blue-50 text-sm font-semibold leading-5 text-[#333333]"
                    : "bg-blue-100 text-xs font-medium text-blue-700"
                )}
              >
                {getInitials(user)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-[#1A1823] truncate">
                {displayName}
              </span>
              <span className="text-xs text-[#676571] truncate">
                {user.email}
              </span>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-[rgba(26,24,35,0.16)] my-1" />

          {/* Menu Items */}
          <DropdownMenu.Item asChild>
            <Link
              to="/dashboard/accounts"
              id="dasboard-manage-social-accounts"
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium text-[#1A1823] cursor-pointer outline-none hover:bg-neutral-50 focus:bg-neutral-50"
            >
              <i className="fa-regular fa-share-nodes w-[18px] text-center text-[#676571]" />
              Manage Social Accounts &amp; Groups
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link
              to="/dashboard/users"
              id="dashboard-manage-users"
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium text-[#1A1823] cursor-pointer outline-none hover:bg-neutral-50 focus:bg-neutral-50"
            >
              <i className="fa-regular fa-users w-[18px] text-center text-[#676571]" />
              Manage Users
            </Link>
          </DropdownMenu.Item>

          {/* Divider */}
          <div className="h-px bg-[rgba(26,24,35,0.16)] my-1" />

          <DropdownMenu.Item asChild>
            <Link
              to="/dashboard/profile"
              id="dashboard-settings"
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium text-[#1A1823] cursor-pointer outline-none hover:bg-neutral-50 focus:bg-neutral-50"
            >
              <i className="fa-regular fa-gear w-[18px] text-center text-[#676571]" />
              Settings
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link
              to="/dashboard/profile/referral"
              id="dashboard-referral-program"
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium text-[#1A1823] cursor-pointer outline-none hover:bg-neutral-50 focus:bg-neutral-50"
            >
              <i className="fa-regular fa-gift w-[18px] text-center text-[#676571]" />
              Referral Program
            </Link>
          </DropdownMenu.Item>

          {/* Divider */}
          <div className="h-px bg-[rgba(26,24,35,0.16)] my-1" />

          <DropdownMenu.Item
            className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium text-[#1A1823] cursor-pointer outline-none hover:bg-neutral-50 focus:bg-neutral-50"
            onClick={logout}
            data-testid="logout-button"
            id="dashboard-logout"
          >
            <i className="fa-regular fa-arrow-right-from-bracket w-[18px] text-center text-[#676571]" />
            Logout
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
